import { Button } from 'react-bootstrap';
import { DeliveryCompanySelect, deliveryCompanyLabel } from './DeliveryCompanySelect';
import type { LineTrackingCourier } from '@/infrastructure/FulfillmentAdminClient';

interface DeliveryCompanyFieldProps {
  lineNo: number;
  parcels: LineTrackingCourier[];
  company: string;
  editing: boolean;
  targetId?: string | undefined;
  disabled?: boolean | undefined;
  adding?: boolean | undefined;
  onCompanyChange: (value: string) => void;
  onTargetChange: (id: string) => void;
  onDelete?: (id: string) => void;
  onAdd?: () => void;
}

const enteredBy = (id: string) => {
  if (/^line#\d+#\d+#AGENT(?:#[a-z0-9-]+)?$/i.test(id)) {
    return 'Admin';
  }
  if (/^ord#\d+#IN$/.test(id)) {
    return 'Migrated from V1';
  }
  return 'Gig worker';
};

/** Every parcel remains visible; selection or deletion targets one parcel, while Add creates a new one. */
export function DeliveryCompanyField({
  lineNo,
  parcels,
  company,
  editing,
  targetId,
  disabled,
  adding,
  onCompanyChange,
  onTargetChange,
  onDelete,
  onAdd,
}: DeliveryCompanyFieldProps) {
  return (
    <div className="d-flex flex-column gap-2" aria-label={`Delivery companies for line ${lineNo}`}>
      {parcels.map((parcel) => (
        <div key={parcel.trackingId}>
          <div className="small font-monospace">{parcel.number}</div>
          <div className="small text-muted">Entered by: {enteredBy(parcel.trackingId)}</div>
          {editing && targetId === parcel.trackingId ? (
            <DeliveryCompanySelect
              label={`Delivery company for waybill ${parcel.number}`}
              value={company}
              onChange={onCompanyChange}
              disabled={disabled}
            />
          ) : (
            <div>{deliveryCompanyLabel(parcel.carrier)}</div>
          )}
          {editing && (
            <div className="d-flex gap-1 mt-1">
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={disabled}
                aria-label={`Edit waybill ${parcel.number}`}
                onClick={() => onTargetChange(parcel.trackingId)}
              >
                Edit
              </Button>
              <Button
                size="sm"
                variant="outline-danger"
                disabled={disabled}
                aria-label={`Delete waybill ${parcel.number}`}
                onClick={() => onDelete?.(parcel.trackingId)}
              >
                Delete
              </Button>
            </div>
          )}
        </div>
      ))}
      {editing && (adding || parcels.length === 0) ? (
        <DeliveryCompanySelect
          label={`Delivery company for line ${lineNo}`}
          value={company}
          onChange={onCompanyChange}
          disabled={disabled}
        />
      ) : parcels.length === 0 ? (
        <>{deliveryCompanyLabel(company)}</>
      ) : null}
      {editing && !adding && (
        <Button size="sm" variant="outline-primary" disabled={disabled} onClick={onAdd}>
          + Add waybill
        </Button>
      )}
    </div>
  );
}
